import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkle, X, PaperPlaneTilt, Trash, Plus } from "@phosphor-icons/react";

/**
 * BidDrafterModal — admin-only AI-powered bid drafter.
 *
 * 3-step wizard:
 *   1. Draft: call POST /jobs/{id}/bid-draft → Claude Vision reads Before
 *      photos → returns scope + materials + labor. Office can add an
 *      optional "hint" (e.g. "focus on plumbing").
 *   2. Edit: office tweaks any field (scope, add/remove/edit materials,
 *      change labor hours, tax). Server recomputes totals on save.
 *   3. Send: mints a public share token, emails landlord via Resend.
 *      Landlord signs on /public/bid/:token — WO flips to
 *      quote_status='approved' with a signed audit record.
 *
 * Props:
 *   jobId, jobTitle
 *   onClose()
 *   onSaved()  — parent should refetch the WO after a bid change
 */
export default function BidDrafterModal({ jobId, jobTitle, onClose, onSaved }) {
  const [step, setStep] = useState("intro"); // intro | drafting | edit | sending | sent
  const [hint, setHint] = useState("");
  const [draft, setDraft] = useState(null);
  const [bid, setBid] = useState(null);
  const [savedBid, setSavedBid] = useState(null); // persisted server record
  const [busy, setBusy] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sentResult, setSentResult] = useState(null);

  const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const runDraft = async () => {
    setBusy(true);
    setStep("drafting");
    try {
      const { data } = await api.post(`/jobs/${jobId}/bid-draft`, { hint });
      setDraft(data);
      setBid({
        scope: data.scope || "",
        materials: (data.materials || []).map(m => ({ ...m })),
        labor_hours: data.labor_hours || 0,
        labor_rate: data.labor_rate || 95,
        tax_rate: 0,
        notes: "",
        valid_days: 30,
      });
      setStep("edit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
      setStep("intro");
    } finally {
      setBusy(false);
    }
  };

  const computeTotals = (b) => {
    const mats = b.materials || [];
    const sm = mats.reduce((n, m) => n + (Number(m.qty) || 0) * (Number(m.unit_cost) || 0), 0);
    const sl = (Number(b.labor_hours) || 0) * (Number(b.labor_rate) || 95);
    const tax = (sm + sl) * (Number(b.tax_rate) || 0);
    return {
      subtotal_materials: Math.round(sm * 100) / 100,
      subtotal_labor: Math.round(sl * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round((sm + sl + tax) * 100) / 100,
    };
  };

  const totals = bid ? computeTotals(bid) : null;

  const updateMat = (i, patch) => {
    setBid(prev => ({
      ...prev,
      materials: prev.materials.map((m, idx) => idx === i ? { ...m, ...patch } : m),
    }));
  };
  const removeMat = (i) => setBid(prev => ({ ...prev, materials: prev.materials.filter((_, idx) => idx !== i) }));
  const addMat = () => setBid(prev => ({ ...prev, materials: [...(prev.materials || []), { name: "", qty: 1, unit: "ea", unit_cost: 0, total: 0 }] }));

  const save = async () => {
    if (!bid.scope.trim()) { toast.error("Scope can't be empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/bid`, bid);
      setSavedBid(data.bid);
      toast.success("Bid saved as draft");
      setStep("sending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!sendTo.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendTo)) {
      toast.error("Enter a valid landlord email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/bid/${savedBid.bid_id}/send`, {
        to_email: sendTo,
        message: sendMsg,
      });
      setSentResult(data);
      toast.success(`Bid emailed to ${sendTo}`);
      setStep("sent");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!sentResult?.url) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const abs = sentResult.url.startsWith("http") ? sentResult.url : `${base}${sentResult.url}`;
    try {
      await navigator.clipboard.writeText(abs);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — highlight and copy manually"); }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-3" data-testid="bid-drafter-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <header className="flex items-center gap-3 p-4 border-b border-slate-200">
          <Sparkle size={20} weight="fill" className="text-[#00C4CC]"/>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">AI Bid Drafter</div>
            <div className="text-sm font-black text-[#0F1F3A] truncate">{jobTitle}</div>
          </div>
          <button onClick={onClose} data-testid="bid-drafter-close"
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600">
            <X size={20}/>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {step === "intro" && (
            <div className="space-y-4" data-testid="bid-drafter-intro">
              <div className="text-sm text-slate-700 leading-relaxed">
                Claude Vision will look at every Before photo on this WO and draft a full bid — scope of work, materials list with quantities and typical retail prices, labor hours, and a total. You'll be able to edit everything before it goes to the landlord.
              </div>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Optional office hint</span>
                <textarea value={hint} onChange={(e) => setHint(e.target.value)}
                  data-testid="bid-drafter-hint"
                  rows={2}
                  placeholder="e.g. Focus on plumbing only. Assume drywall patch needed."
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#00C4CC]"/>
              </label>
              <button onClick={runDraft} disabled={busy}
                data-testid="bid-drafter-draft"
                className="w-full h-11 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                <Sparkle size={14} weight="fill"/>
                Draft with AI
              </button>
              <div className="text-[10px] text-slate-500 text-center">Uses Claude Vision. Costs ~$0.02 per draft.</div>
            </div>
          )}

          {step === "drafting" && (
            <div className="py-12 text-center" data-testid="bid-drafter-loading">
              <div className="inline-block h-10 w-10 border-4 border-[#00C4CC]/30 border-t-[#00C4CC] rounded-full animate-spin mb-4"/>
              <div className="text-sm font-black uppercase tracking-widest text-slate-600">Reading photos & drafting…</div>
              <div className="text-xs text-slate-500 mt-1">This takes 10-20 seconds.</div>
            </div>
          )}

          {step === "edit" && bid && (
            <div className="space-y-4" data-testid="bid-drafter-edit">
              {draft?.confidence != null && (
                <div className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded inline-flex ${
                  draft.confidence >= 70 ? "bg-emerald-100 text-emerald-700" :
                  draft.confidence >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                }`}>
                  AI confidence · {draft.confidence}% · {draft.photos_used} photos
                </div>
              )}

              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Scope of work</span>
                <textarea value={bid.scope} onChange={(e) => setBid({ ...bid, scope: e.target.value })}
                  data-testid="bid-drafter-scope"
                  rows={4}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#00C4CC]"/>
              </label>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Materials</span>
                  <button onClick={addMat} data-testid="bid-drafter-add-material"
                    className="h-6 px-2 rounded-full bg-slate-100 hover:bg-slate-200 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                    <Plus size={11} weight="bold"/> Add
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(bid.materials || []).map((m, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`bid-drafter-material-${i}`}>
                      <input value={m.name} onChange={(e) => updateMat(i, { name: e.target.value })}
                        placeholder="Material" className="flex-1 h-9 px-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                      <input type="number" step="any" value={m.qty || 0} onChange={(e) => updateMat(i, { qty: Number(e.target.value) })}
                        className="w-14 h-9 px-2 rounded border border-slate-200 text-sm text-center focus:outline-none focus:border-[#00C4CC]"/>
                      <input value={m.unit || ""} onChange={(e) => updateMat(i, { unit: e.target.value })}
                        placeholder="ea" className="w-12 h-9 px-2 rounded border border-slate-200 text-sm text-center focus:outline-none focus:border-[#00C4CC]"/>
                      <div className="text-[10px] text-slate-400 font-bold">$</div>
                      <input type="number" step="any" value={m.unit_cost || 0} onChange={(e) => updateMat(i, { unit_cost: Number(e.target.value) })}
                        className="w-20 h-9 px-2 rounded border border-slate-200 text-sm text-right focus:outline-none focus:border-[#00C4CC]"/>
                      <button onClick={() => removeMat(i)} className="h-9 w-9 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center">
                        <Trash size={14}/>
                      </button>
                    </div>
                  ))}
                  {(bid.materials || []).length === 0 && (
                    <div className="text-xs italic text-slate-400 text-center py-3">No materials — labor only</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Labor hrs</span>
                  <input type="number" step="0.25" value={bid.labor_hours} onChange={(e) => setBid({ ...bid, labor_hours: Number(e.target.value) })}
                    data-testid="bid-drafter-labor-hours"
                    className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                </label>
                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">$ / hr</span>
                  <input type="number" step="1" value={bid.labor_rate} onChange={(e) => setBid({ ...bid, labor_rate: Number(e.target.value) })}
                    data-testid="bid-drafter-labor-rate"
                    className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                </label>
                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Tax %</span>
                  <input type="number" step="0.01" value={bid.tax_rate * 100}
                    onChange={(e) => setBid({ ...bid, tax_rate: Number(e.target.value) / 100 })}
                    data-testid="bid-drafter-tax"
                    className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                </label>
              </div>

              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Landlord-facing notes (optional)</span>
                <textarea value={bid.notes} onChange={(e) => setBid({ ...bid, notes: e.target.value })}
                  data-testid="bid-drafter-notes"
                  rows={2}
                  placeholder="e.g. Includes disposal. Excludes any hidden damage discovered during repair."
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#00C4CC]"/>
              </label>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
                <Row2 l="Materials" r={money(totals.subtotal_materials)}/>
                <Row2 l={`Labor · ${bid.labor_hours}h @ ${money(bid.labor_rate)}/h`} r={money(totals.subtotal_labor)}/>
                {totals.tax > 0 && <Row2 l={`Tax (${(bid.tax_rate * 100).toFixed(2)}%)`} r={money(totals.tax)}/>}
                <div className="flex justify-between pt-2 border-t border-slate-200 text-base font-black" data-testid="bid-drafter-total">
                  <span>Total</span>
                  <span>{money(totals.total)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setStep("intro")} data-testid="bid-drafter-redraft"
                  className="h-11 px-4 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-black uppercase tracking-widest text-xs">
                  Re-draft
                </button>
                <button onClick={save} disabled={busy}
                  data-testid="bid-drafter-save"
                  className="flex-1 h-11 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                  {busy ? "Saving…" : "Save & Next →"}
                </button>
              </div>
            </div>
          )}

          {step === "sending" && savedBid && (
            <div className="space-y-4" data-testid="bid-drafter-send">
              <div className="text-sm text-slate-700 leading-relaxed">
                Bid saved. Enter the landlord's email — they'll get a review link and can sign with a typed signature. On sign, this WO flips to <b>Quoted-Approved</b>.
              </div>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Landlord email</span>
                <input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                  data-testid="bid-drafter-send-email"
                  placeholder="landlord@company.com"
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 px-3 text-sm focus:outline-none focus:border-[#00C4CC]"/>
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Message (optional)</span>
                <textarea value={sendMsg} onChange={(e) => setSendMsg(e.target.value)}
                  data-testid="bid-drafter-send-msg"
                  rows={3}
                  placeholder="e.g. Here's the estimate for the leak we found today. Please sign to approve and we'll schedule the fix."
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#00C4CC]"/>
              </label>
              <button onClick={send} disabled={busy}
                data-testid="bid-drafter-send-btn"
                className="w-full h-11 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                <PaperPlaneTilt size={14} weight="fill"/>
                {busy ? "Sending…" : "Send to Landlord"}
              </button>
            </div>
          )}

          {step === "sent" && sentResult && (
            <div className="text-center py-8 space-y-4" data-testid="bid-drafter-sent">
              <div className="text-5xl">✉️</div>
              <div className="text-lg font-black text-[#0F1F3A]">Bid sent to {sendTo}</div>
              <div className="text-sm text-slate-600">They can review + sign at any time. You'll see this WO flip to <b>Quoted-Approved</b> once they've signed.</div>
              <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono break-all text-left">
                {sentResult.url}
              </div>
              <div className="flex gap-2 justify-center">
                <button onClick={copyUrl} data-testid="bid-drafter-copy"
                  className="h-10 px-4 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-black uppercase tracking-widest text-xs">Copy link</button>
                <button onClick={onClose} data-testid="bid-drafter-done"
                  className="h-10 px-4 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-xs">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row2({ l, r }) {
  return (
    <div className="flex justify-between text-[13px]">
      <span className="text-slate-600">{l}</span>
      <span className="font-semibold">{r}</span>
    </div>
  );
}
